import {
  getCoachingPlan,
  getSavedConversationsForChat,
  selectActiveCoachingPlan,
  selectGetCoachingPlanStatus,
  setChatbotMessages,
  setIsCoachbotOpen,
  setThreadId,
  selectGetRecurringChatStatus,
  getRecurringChat,
  selectRecurringChat,
  selectProcessConversationStatus,
} from "app/containers/Chatbot/slice";
import Robot from "resources/images/robot.png";
import Button from "app/storybookComponents/Button";
import Loading from "app/storybookComponents/Loading";
import { useEffect, useMemo, useState } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScheduleChatbotRemindersModal from "app/components/Modals/ScheduleChatbotRemindersModal";
import { useNavigate } from "react-router-dom";
import EditSkillFocusModal from "app/components/Modals/EditSkillFocusModal";
import {
  selectConfigCatFlag,
  selectCurrentUserInfo,
  selectIsCurrentUserAdmin,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import ExpandableLineClamp from "app/storybookComponents/CollapsibleCards/ExpandableLineClamp";
import { selectHasCoachBoConversationHistoryEnabled } from "app/containers/AdminConsole/slice";
import {
  COACH_BO_DAY_MESSAGES,
  getDayOfWeek,
} from "app/containers/Dashboard/coachBoWelcomeMessages";
import TodaysMantraCard from "./TodaysMantraCard";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import {
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
} from "app/containers/WeeklyCheckInMonitor/slice";
import { selectAllCoachBoCheckInNotifications } from "app/components/Notifications/slice";
import { ChatNotification } from "app/components/Notifications/types";
import { getIsWeeklyCheckInEnabledForCurrentUser } from "../helpers";
import { selectAllUserSubordinates } from "app/containers/UserSettings/slice";
import { faArrowsToDot } from "@fortawesome/free-solid-svg-icons";
import { onCoachBoCheckInNotificationActionClick } from "app/components/Notifications/helpers";
import { onFirstCoachBoChatClick } from "app/containers/Chatbot/helpers";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";

interface CoachBoGreeterCardProps {
  name?: string;
}

export default function CoachBoGreeterCard({
  name,
}: Readonly<CoachBoGreeterCardProps>) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ------------------------------ Selectors ------------------------------ //
  const activeCoachingPlan = useAppSelector(selectActiveCoachingPlan);
  const getCoachingPlanStatus = useAppSelector(selectGetCoachingPlanStatus);
  const getRecurringChatStatus = useAppSelector(selectGetRecurringChatStatus);
  const activeRecurringChat = useAppSelector(selectRecurringChat);
  const userInfo = useAppSelector(selectCurrentUserInfo);
  const isConversationHistoryEnabledCompanyWide = useAppSelector(
    selectHasCoachBoConversationHistoryEnabled
  );
  const processConversationStatus = useAppSelector(
    selectProcessConversationStatus
  );
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const allCoachBoCheckInNotifications = useAppSelector(
    selectAllCoachBoCheckInNotifications
  );
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const weeklyCheckInCustomList = useAppSelector(selectWeeklyCheckInCustomList);

  // ------------------------------ useState ------------------------------ //
  const [showScheduleReminderModal, setShowScheduleReminderModal] =
    useState(false);
  const [showEditSkillFocusModal, setShowEditSkillFocusModal] = useState(false);

  // ------------------------------ useEffect ------------------------------ //
  useEffect(() => {
    if (getCoachingPlanStatus !== "idle") {
      return;
    }
    dispatch(getCoachingPlan());
  }, [dispatch, getCoachingPlanStatus]);

  useEffect(() => {
    if (getRecurringChatStatus !== "idle") {
      return;
    }
    dispatch(getRecurringChat());
  }, [dispatch, getRecurringChatStatus]);

  // --------------- Render Functions ---------------
  const isConversationHistoryEnabled =
    userInfo?.hasCoachBoConversationHistoryEnabled &&
    isConversationHistoryEnabledCompanyWide;

  const doesUserHaveWeeklyCheckInEnabled = useMemo(() => {
    return getIsWeeklyCheckInEnabledForCurrentUser(
      userInfo?.userAccountId ?? 0,
      weeklyCheckInCompanySettings,
      weeklyCheckInCustomList,
      weeklyCheckInConfigCatFlag,
      allUsersSubordinates,
      teamsById
    );
  }, [
    weeklyCheckInCompanySettings,
    weeklyCheckInCustomList,
    weeklyCheckInConfigCatFlag,
    allUsersSubordinates,
    teamsById,
    userInfo?.userAccountId,
  ]);

  const weeklyCheckInNotification: ChatNotification | null = useMemo(() => {
    const notification = allCoachBoCheckInNotifications[0];
    return notification;
  }, [allCoachBoCheckInNotifications]);

  const onStartFirstChatClick = () => {
    dispatch(setIsCoachbotOpen(true));
    if (userInfo?.hasCoachBoConversationHistoryEnabled) {
      void onFirstCoachBoChatClick(dispatch);
    }
  };

  const onChatWithBoClick = async () => {
    dispatch(setIsCoachbotOpen(true));
    const { conversations, isStale } = await dispatch(
      getSavedConversationsForChat()
    ).unwrap();
    if (isStale) return;
    const latestConversation = conversations.find((c) => !c.weeklyCheckInLink);
    if (!latestConversation) {
      return onStartFirstChatClick();
    }
    dispatch(setThreadId(latestConversation.threadId));
    dispatch(setChatbotMessages(latestConversation.messages));
  };

  const safeParse = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return jsonString;
    }
  };

  const onDropdownSelect = (eventKey: string | null) => {
    switch (eventKey) {
      case "viewPastChats":
        return navigate("/UserGuide?tab=Chat+History");
      case "notificationConfiguration":
        // if the user is an admin and the conversation history is turned off companywide redirect to admin console settings
        return navigate(
          isAdmin && !isConversationHistoryEnabledCompanyWide
            ? "/AdminConsole/Settings"
            : "/UserSettings?userSettingsActiveTab=emailAndNotifications"
        );
      case "editSkillFocus":
        return setShowEditSkillFocusModal(true);
      case "recurringChat":
        return setShowScheduleReminderModal(true);
      default:
        return;
    }
  };

  const getDropdown = (hasActiveCoachingPlan?: boolean) => {
    // If the conversation history is disabled company wide and the user is not an admin no need to show the dropdown
    if (!isConversationHistoryEnabledCompanyWide && !isAdmin) {
      return null;
    }

    return (
      <Dropdown
        onSelect={onDropdownSelect}
        placement="auto"
        style={{ position: "absolute", top: "20px", right: "20px" }}
      >
        <Dropdown.Toggle
          variant="outline-primary"
          id="dropdown-basic"
          className="no-caret"
        >
          <FontAwesomeIcon icon="ellipsis" size="lg" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {isConversationHistoryEnabled ? (
            <>
              <Dropdown.Item eventKey="viewPastChats">
                View recent chats
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          ) : null}
          <Dropdown.Item eventKey="recurringChat">
            {activeRecurringChat ? "Edit" : "Schedule"} chat reminder
          </Dropdown.Item>
          {hasActiveCoachingPlan && isConversationHistoryEnabled ? (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="editSkillFocus">
                Edit current focus
              </Dropdown.Item>
            </>
          ) : null}
          <Dropdown.Divider />
          {isConversationHistoryEnabledCompanyWide || isAdmin ? (
            <Dropdown.Item eventKey="notificationConfiguration">
              Configure chat settings
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getProcessingConversationFocusCard = () => {
    return (
      <Card className="coach-bo-greeter-card main-card">
        <div className="warning-banner blue border-0 w-100 row-gap-20px align-items-center">
          <div>
            <Loading color="#202d63" />
          </div>
          <div className="column-gap-8px">
            <p className="sapphire-150-text fw-bold">
              Your conversation is processing...
            </p>
            <p>
              This will only take about 15 seconds. If it takes more than 30
              seconds, please refresh the page.
            </p>
          </div>
        </div>
      </Card>
    );
  };

  const getCoachingPlanCardHeader = (titleText: string) => {
    return (
      <div className="row-gap-8px align-items-center">
        <div className="circle-icon-container">
          <FontAwesomeIcon icon={faArrowsToDot} />
        </div>
        <h3>{titleText}</h3>
      </div>
    );
  };

  const getChatWithCoachBoCTA = () => {
    if (!doesUserHaveWeeklyCheckInEnabled) {
      return <Button onClick={onChatWithBoClick}>Private Coaching</Button>;
    }

    // A check-in is actually available for this user right now if, and only
    // if, the backend has surfaced a notification for it — that's real
    // check-in state, unlike the weekday math this used to gate on.
    // TODO (DBC-669): swap for a dedicated cycle-status endpoint once it
    // exists, e.g.:
    //   {
    //     isIncludedInCurrentCheckInCycle: boolean,
    //     hasCheckInCompletedForCurrentCycle: boolean,
    //     nextCheckInCycleStart: string | null,
    //     nextCheckInCycleStartCheckInDate: string | null,
    //     activeCheckInCycleThreadId: string | null,
    //   }
    const hasActiveCheckIn = !!weeklyCheckInNotification;

    return (
      <div className="column-gap-8px">
        <div className="row-gap-8px">
          <Button
            onClick={() => {
              onCoachBoCheckInNotificationActionClick(
                weeklyCheckInNotification,
                dispatch
              );
            }}
            disabled={!hasActiveCheckIn}
          >
            Manager Check-in
          </Button>
          <Button onClick={onChatWithBoClick} variant="secondary-blue">
            Private Coaching
          </Button>
        </div>
        <p style={{ fontWeight: 600 }}>
          {hasActiveCheckIn ? (
            <p>
              Complete your check-in to ensure your manager receives insights.
            </p>
          ) : (
            <p>
              {/* TODO (DBC-669): show the real next-check-in date here once
                  AI-Service exposes it (loginless.ts fix + DBC-704 /
                  isIncludedInCurrentCheckInCycle, nextCheckInCycleStart).
                  Until then we only know the company-wide in-progress count
                  from DBC-657, which is the best signal available. */}
              {weeklyCheckInCompanySettings?.inProgressCount ? (
                <>
                  Your next check-in will open soon —{" "}
                  {weeklyCheckInCompanySettings.inProgressCount}{" "}
                  {weeklyCheckInCompanySettings.inProgressCount === 1
                    ? "teammate is"
                    : "teammates are"}{" "}
                  currently completing theirs.
                </>
              ) : (
                "Your next check-in will open soon."
              )}
            </p>
          )}
        </p>
      </div>
    );
  };

  const getYourFocusCard = () => {
    const dropdown = getDropdown(!!activeCoachingPlan);
    if (processConversationStatus === "loading") {
      return getProcessingConversationFocusCard();
    }

    if (getCoachingPlanStatus === "loading") {
      return (
        <Card className="coach-bo-greeter-card main-card">
          <div className="column-gap-16px w-100">
            {getCoachingPlanCardHeader("Your focus right now")}
            <Skeleton className="h-20 w-full" />
          </div>
        </Card>
      );
    }

    return (
      <Card className="coach-bo-greeter-card main-card">
        <div className="column-gap-16px w-100">
          <div className="column-gap-8px">
            {getCoachingPlanCardHeader("Your focus right now")}
            <ExpandableLineClamp
              text={
                activeCoachingPlan?.currentFocus
                  ? safeParse(activeCoachingPlan?.currentFocus)
                  : "Hey there! I'm Coach Bo, an AI-driven coaching assistant, and I'm here to help you be your best at work. Help me understand your career goals so I can create a personalized development plan for you. Start on your goals now?"
              }
              lines={2}
            />
          </div>
          <div className="action-buttons mt-auto">
            <div className="row-gap-12px">{getChatWithCoachBoCTA()}</div>
            {dropdown}
          </div>
        </div>
      </Card>
    );
  };

  const coachBoGreeting = useMemo(() => {
    const dayOfWeek = new Date().getDay();
    const dayOfWeekName = getDayOfWeek(dayOfWeek);

    let greeting = `Happy ${dayOfWeekName}, ${name}!`;
    const message = COACH_BO_DAY_MESSAGES[dayOfWeekName] ?? [];
    let randomMessage =
      message[Math.floor(Math.random() * message.length)]?.message;
    randomMessage = randomMessage?.replace("$f_name", name ?? "");

    return (
      <div className="coach-bo-greeting">
        <div className="coach-bo-greeting__avatar">
          <img src={Robot} alt="coach-bo" />
        </div>
        <div>
          <h1>{greeting}</h1>
          <p className="sapphire-150-text">{randomMessage}</p>
        </div>
      </div>
    );
  }, [name]);

  return (
    <div className="coach-bo-greeter-card-container">
      <ScheduleChatbotRemindersModal
        show={showScheduleReminderModal}
        onHide={() => setShowScheduleReminderModal(false)}
        recurringChat={activeRecurringChat}
      />
      <EditSkillFocusModal
        show={showEditSkillFocusModal}
        onHide={() => setShowEditSkillFocusModal(false)}
      />
      {coachBoGreeting}
      <div className="coach-bo-greeter-cards">
        {getYourFocusCard()}
        <TodaysMantraCard />
      </div>
    </div>
  );
}
